import { membershipAllows } from '../membership';
import { getResolvedPostFlightStatus, isLogbookCompleted } from '../booking/activeFlightSelection';
import {
  getBookingEndDate,
  getBookingStartDate,
} from '../booking/bookingUtils';

export function getFlightFlowProgressState({
  booking,
  membershipTier,
  preFlightProgressState,
  preflightComplete,
  weightBalanceComplete,
  liveFlightActive,
  closeFlightComplete,
  logbookComplete,
}) {
  const hasBooking = Boolean(booking);
  const flowState = getFlowState(booking);
  const resolvedPreFlightState =
    preFlightProgressState ||
    (preflightComplete ? 'completed' : 'not_started');
  const weightBalanceStageComplete =
    Boolean(weightBalanceComplete) ||
    flowState === 'post_complete';
  const preFlightStageComplete =
    resolvedPreFlightState === 'completed' ||
    flowState === 'post_complete';

  const logbookAvailable = membershipTier
    ? membershipAllows(membershipTier, 'pilot-logbook')
    : true;

  const resolvedLogbookComplete =
    typeof logbookComplete === 'boolean'
      ? logbookComplete
      : inferLogbookComplete(booking);

  const applicableStages = [
    {
      id: 'book-flight',
      label: 'Book Flight',
      complete: hasBooking,
      optional: false,
      applicable: true,
      instruction: 'Book Flight',
    },
    {
      id: 'weight-balance',
      label: 'Weight & Balance',
      complete: hasBooking && weightBalanceStageComplete,
      optional: false,
      applicable: hasBooking,
      instruction: 'Review Weight & Balance',
    },
    {
      id: 'pre-flight',
      label: 'Pre-Flight',
      complete: hasBooking && preFlightStageComplete,
      optional: false,
      applicable: hasBooking,
      instruction: 'Complete Pre-Flight',
    },
    {
      id: 'live-flight',
      label: 'Live Flight',
      complete:
        hasBooking &&
        Boolean(liveFlightActive) &&
        (flowState === 'in_progress' ||
          flowState === 'landed' ||
          flowState === 'post_complete'),
      optional: true,
      applicable: hasBooking,
      instruction: 'Start Live Flight',
    },
    {
      id: 'close-flight',
      label: 'Close Flight',
      complete:
        hasBooking &&
        (Boolean(closeFlightComplete) || flowState === 'post_complete'),
      optional: false,
      applicable: hasBooking,
      instruction: 'Close Flight',
    },
    {
      id: 'logbook',
      label: 'Logbook',
      complete: hasBooking && resolvedLogbookComplete,
      optional: false,
      applicable: hasBooking && logbookAvailable,
      instruction: 'Complete Logbook',
    },
  ].filter((stage) => stage.applicable);

  const currentIndex = applicableStages.findIndex(
    (stage) => !stage.complete && !stage.optional
  );

  const fallbackOptionalIndex =
    currentIndex === -1
      ? applicableStages.findIndex((stage) => !stage.complete)
      : -1;

  const resolvedCurrentIndex =
    currentIndex >= 0 ? currentIndex : fallbackOptionalIndex;

  const stages = applicableStages.map((stage, index) => ({
    ...stage,
    status: stage.complete
      ? 'complete'
      : index === resolvedCurrentIndex
      ? 'current'
      : stage.optional
      ? 'optional'
      : 'pending',
  }));

  const completedCount = stages.filter(
    (stage) => stage.complete
  ).length;

  const progress =
    stages.length > 0
      ? Math.round((completedCount / stages.length) * 100)
      : 0;

  const currentStage =
    resolvedCurrentIndex >= 0
      ? stages[resolvedCurrentIndex]
      : null;

  const nextInstruction = currentStage
    ? currentStage.instruction
    : 'Flight complete';

  const status =
    progress >= 100
      ? 'Complete'
      : hasBooking
      ? 'In progress'
      : 'Not Started';

  return {
    stages,
    progress,
    status,
    nextInstruction,
    currentStage,
    logbookAvailable,
  };
}

export function getFlowState(nextFlight) {
  if (!nextFlight) return 'none';

  const { startDateTime, endDateTime } = getFlightDateRange(nextFlight);
  const postFlightCompleted = Boolean(
    getResolvedPostFlightStatus(nextFlight)
  );
  const now = new Date();

  if (startDateTime && now < startDateTime) return 'booked';

if (
  startDateTime &&
  now >= startDateTime &&
  postFlightCompleted
) {
  return 'post_complete';
}

if (
  startDateTime &&
  endDateTime &&
  now >= startDateTime &&
  now <= endDateTime
) {
  return 'in_progress';
}

if (startDateTime && now > startDateTime) {
  return 'landed';
}

  return 'booked';
}

function inferLogbookComplete(booking) {
  // A flight that never happened has no logbook entry to complete.
  if (getResolvedPostFlightStatus(booking) === 'did_not_fly') {
    return true;
  }

  return isLogbookCompleted(booking);
}

function getFlightDateRange(nextFlight) {
  const startDateTime = getBookingStartDate(nextFlight);
  const endDateTime = getBookingEndDate(nextFlight);
  return { startDateTime, endDateTime };
}
